/**
 * Warehouse Property Type Handler
 * 
 * Specific implementation for Warehouse properties
 * Warehouses are industrial/logistics properties for storage and distribution
 */

import { PropertyType } from '../../../shared/definitions/property';
import { CommercialPropertyHandler } from '../base/commercial-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';
import { PropertyValidationRule } from '../base/property-type-handler.abstract';

export class WarehouseHandler extends CommercialPropertyHandler {
  readonly propertyType = PropertyType.Warehouse;
  readonly displayName = 'Warehouse';
  readonly pluralName = 'Warehouses';
  readonly icon = 'warehouse';
  readonly description = 'Industrial storage and distribution facility';

  /**
   * Warehouse-specific room types
   * Focus on storage zones, loading areas, and office spaces
   */
  getRoomTypes(): RoomTypeConfig[] {
    return [
      {
        typeKey: 'warehouse_section',
        label: 'Warehouse Section',
        pluralLabel: 'Warehouse Sections',
        countField: 'warehouseSections',
        icon: 'inventory',
        defaultCount: 0,
        description: 'Distinct storage or operational section within the warehouse',
        formFields: [
          {
            name: 'name',
            label: 'Section Name',
            type: 'text',
            required: true,
            placeholder: 'e.g., Zone A, Cold Storage, Receiving Area'
          },
          {
            name: 'sqm',
            label: 'Surface Area',
            type: 'number',
            suffix: 'm²',
            required: true,
            min: 10,
            max: 50000,
            step: 1
          },
          {
            name: 'ceilingHeight',
            label: 'Ceiling Height',
            type: 'number',
            suffix: 'm',
            min: 2,
            max: 30,
            step: 0.1,
            hint: 'Clear height available for storage'
          },
          {
            name: 'floorLoadCapacity',
            label: 'Floor Load Capacity',
            type: 'number',
            suffix: 'kg/m²',
            min: 100,
            max: 10000,
            hint: 'Maximum weight per square meter'
          },
          {
            name: 'sectionType',
            label: 'Section Type',
            type: 'select',
            options: [
              { value: 'general_storage', label: 'General Storage' },
              { value: 'cold_storage', label: 'Cold Storage' },
              { value: 'hazmat', label: 'Hazardous Materials' },
              { value: 'receiving', label: 'Receiving Area' },
              { value: 'dispatch', label: 'Dispatch Area' },
              { value: 'picking', label: 'Picking Area' },
              { value: 'packing', label: 'Packing Area' }
            ]
          },
          {
            name: 'hasRacking',
            label: 'Has Racking System',
            type: 'checkbox',
            default: false
          },
          {
            name: 'rackingType',
            label: 'Racking Type',
            type: 'select',
            options: [
              { value: 'pallet', label: 'Pallet Racking' },
              { value: 'cantilever', label: 'Cantilever' },
              { value: 'drive_in', label: 'Drive-In' },
              { value: 'push_back', label: 'Push Back' },
              { value: 'flow', label: 'Flow Racking' }
            ]
          },
          {
            name: 'hasTemperatureControl',
            label: 'Temperature Controlled',
            type: 'checkbox',
            default: false
          },
          {
            name: 'temperatureRange',
            label: 'Temperature Range',
            type: 'text',
            placeholder: 'e.g., -25°C to +5°C'
          },
          {
            name: 'hasHumidityControl',
            label: 'Humidity Control',
            type: 'checkbox',
            default: false
          },
          {
            name: 'notes',
            label: 'Additional Notes',
            type: 'textarea'
          }
        ]
      },
      {
        typeKey: 'loading_dock',
        label: 'Loading Dock',
        pluralLabel: 'Loading Docks',
        countField: 'loadingDocks',
        icon: 'local_shipping',
        defaultCount: 0,
        description: 'Loading and unloading bay for trucks',
        formFields: [
          {
            name: 'name',
            label: 'Dock Name',
            type: 'text',
            placeholder: 'e.g., Dock 1, North Loading Bay'
          },
          {
            name: 'dockHeight',
            label: 'Dock Height',
            type: 'number',
            suffix: 'm',
            min: 0.8,
            max: 2.0,
            step: 0.1,
            hint: 'Height from ground level'
          },
          {
            name: 'doorWidth',
            label: 'Door Width',
            type: 'number',
            suffix: 'm',
            min: 2,
            max: 10,
            step: 0.1
          },
          {
            name: 'doorHeight',
            label: 'Door Height',
            type: 'number',
            suffix: 'm',
            min: 2,
            max: 8,
            step: 0.1
          },
          {
            name: 'maxTruckSize',
            label: 'Maximum Truck Size',
            type: 'select',
            options: [
              { value: 'van', label: 'Van' },
              { value: 'rigid_7.5t', label: 'Rigid 7.5t' },
              { value: 'rigid_18t', label: 'Rigid 18t' },
              { value: 'articulated', label: 'Articulated' },
              { value: 'mega_trailer', label: 'Mega Trailer' }
            ]
          },
          {
            name: 'hasDockLeveler',
            label: 'Dock Leveler',
            type: 'checkbox',
            default: false
          },
          {
            name: 'levelerType',
            label: 'Leveler Type',
            type: 'select',
            options: [
              { value: 'mechanical', label: 'Mechanical' },
              { value: 'hydraulic', label: 'Hydraulic' },
              { value: 'air_powered', label: 'Air Powered' }
            ]
          },
          {
            name: 'hasDockShelter',
            label: 'Dock Shelter',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasWheelChocks',
            label: 'Wheel Chocks',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasVehicleRestraint',
            label: 'Vehicle Restraint System',
            type: 'checkbox',
            default: false
          },
          {
            name: 'notes',
            label: 'Additional Notes',
            type: 'textarea'
          }
        ]
      },
      {
        typeKey: 'office_space',
        label: 'Office Space',
        pluralLabel: 'Office Spaces',
        countField: 'officeSpaces',
        icon: 'work',
        defaultCount: 0,
        description: 'Administrative office space within the warehouse',
        formFields: [
          {
            name: 'name',
            label: 'Office Name',
            type: 'text',
            placeholder: 'e.g., Main Office, Manager Office'
          },
          {
            name: 'sqm',
            label: 'Surface Area',
            type: 'number',
            suffix: 'm²',
            min: 5,
            max: 500,
            step: 0.1
          },
          {
            name: 'numberOfWorkstations',
            label: 'Number of Workstations',
            type: 'number',
            min: 0,
            max: 50
          },
          {
            name: 'hasReception',
            label: 'Has Reception Area',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasMeetingRoom',
            label: 'Has Meeting Room',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasKitchen',
            label: 'Has Kitchen/Break Room',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasRestroom',
            label: 'Has Restroom',
            type: 'checkbox',
            default: false
          },
          {
            name: 'floorNumber',
            label: 'Floor Number',
            type: 'number',
            min: 0,
            max: 5,
            default: 0,
            hint: '0 = ground floor, 1 = mezzanine, etc.'
          },
          {
            name: 'notes',
            label: 'Additional Notes',
            type: 'textarea'
          }
        ]
      }
    ];
  }

  /**
   * Warehouse-specific features
   */
  getFeatures(): FeatureConfig[] {
    const coreCommercialFeatures = this.getCoreCommercialFeatures();
    
    const warehouseSpecificFeatures: FeatureConfig[] = [
      {
        key: 'racking_system',
        label: 'Racking System',
        icon: 'view_column',
        category: FeatureCategory.PROPERTY_ONLY, // Warehouse-specific feature
        description: 'Industrial racking for pallets and storage',
        detailsInterface: 'RackingSystemDetail',
        formFields: [
          {
            name: 'rackingType',
            label: 'Racking Type',
            type: 'select',
            required: true,
            options: [
              { value: 'selective', label: 'Selective Pallet Racking' },
              { value: 'drive_in', label: 'Drive-In Racking' },
              { value: 'push_back', label: 'Push Back Racking' },
              { value: 'cantilever', label: 'Cantilever Racking' },
              { value: 'flow', label: 'Flow Racking' },
              { value: 'mezz', label: 'Mezzanine Racking' }
            ]
          },
          {
            name: 'numberOfLevels',
            label: 'Number of Levels',
            type: 'number',
            min: 1,
            max: 15
          },
          {
            name: 'maxLoadPerLevel',
            label: 'Max Load Per Level',
            type: 'number',
            suffix: 'kg',
            min: 100
          },
          {
            name: 'totalRackingLength',
            label: 'Total Racking Length',
            type: 'number',
            suffix: 'm'
          },
          {
            name: 'palletPositions',
            label: 'Pallet Positions',
            type: 'number',
            min: 1
          },
          {
            name: 'manufacturer',
            label: 'Manufacturer',
            type: 'text'
          },
          {
            name: 'hasLoadNotices',
            label: 'Has Load Notices',
            type: 'checkbox',
            default: true
          }
        ]
      },
      {
        key: 'overhead_crane',
        label: 'Overhead Crane',
        icon: 'engineering',
        category: FeatureCategory.FLEXIBLE, // Can be building or warehouse-specific
        description: 'Overhead crane system for heavy lifting',
        detailsInterface: 'MaterialHandlingDetail',
        formFields: [
          {
            name: 'liftingCapacity',
            label: 'Lifting Capacity',
            type: 'number',
            suffix: 'tonnes',
            required: true,
            min: 0.1,
            step: 0.1
          },
          {
            name: 'span',
            label: 'Span',
            type: 'number',
            suffix: 'm',
            min: 1
          },
          {
            name: 'numberOfCranes',
            label: 'Number of Cranes',
            type: 'number',
            required: true,
            min: 1,
            default: 1
          },
          {
            name: 'craneType',
            label: 'Crane Type',
            type: 'select',
            options: [
              { value: 'single_girder', label: 'Single Girder' },
              { value: 'double_girder', label: 'Double Girder' },
              { value: 'gantry', label: 'Gantry Crane' },
              { value: 'jib', label: 'Jib Crane' }
            ]
          },
          {
            name: 'manufacturer',
            label: 'Manufacturer',
            type: 'text'
          },
          {
            name: 'installedDate',
            label: 'Installation Date',
            type: 'date'
          },
          {
            name: 'lastInspectionDate',
            label: 'Last Inspection Date',
            type: 'date'
          }
        ]
      },
      {
        key: 'temperature_control',
        label: 'Temperature Control',
        icon: 'ac_unit',
        category: FeatureCategory.FLEXIBLE, // Can be building or warehouse-specific
        description: 'Temperature controlled storage',
        detailsInterface: 'HVACDetail',
        formFields: [
          {
            name: 'systemType',
            label: 'System Type',
            type: 'select',
            required: true,
            options: [
              { value: 'refrigerated', label: 'Refrigerated' },
              { value: 'freezer', label: 'Freezer' },
              { value: 'climate_controlled', label: 'Climate Controlled' },
              { value: 'heated', label: 'Heated Only' }
            ]
          },
          {
            name: 'temperatureRange',
            label: 'Temperature Range',
            type: 'text',
            required: true,
            placeholder: 'e.g., +2°C to +8°C'
          },
          {
            name: 'zoneDivision',
            label: 'Multiple Temperature Zones',
            type: 'checkbox',
            default: false
          },
          {
            name: 'hasHumidityControl',
            label: 'Humidity Control',
            type: 'checkbox',
            default: false
          },
          {
            name: 'installedCapacity',
            label: 'Installed Capacity',
            type: 'number',
            suffix: 'kW'
          },
          {
            name: 'maintenanceCompany',
            label: 'Maintenance Company',
            type: 'text'
          }
        ]
      },
      {
        key: 'forklift_access',
        label: 'Forklift Access',
        icon: 'forklift',
        category: FeatureCategory.PROPERTY_ONLY, // Warehouse-specific
        description: 'Forklift operation capabilities',
        detailsInterface: 'MaterialHandlingDetail',
        formFields: [
          {
            name: 'maxForkliftCapacity',
            label: 'Max Forklift Capacity',
            type: 'number',
            suffix: 'tonnes',
            min: 0.5,
            step: 0.5
          },
          {
            name: 'aisleWidth',
            label: 'Aisle Width',
            type: 'number',
            suffix: 'm',
            min: 2,
            step: 0.1
          },
          {
            name: 'floorType',
            label: 'Floor Type',
            type: 'select',
            options: [
              { value: 'concrete', label: 'Concrete' },
              { value: 'epoxy', label: 'Epoxy Coated' },
              { value: 'anti_dust', label: 'Anti-Dust Treated' }
            ]
          },
          {
            name: 'hasChargingStations',
            label: 'Has Charging Stations',
            type: 'checkbox',
            default: false
          }
        ]
      }
    ];

    return [...coreCommercialFeatures, ...warehouseSpecificFeatures];
  }

  override getMainCharacteristics(): string[] {
    return ['warehouseArea', 'ceilingHeight', 'loadingDocks', 'floorLoadCapacity', 'officeArea'];
  }

  override getValidationRules(): PropertyValidationRule[] {
    const baseRules = super.getValidationRules();
    
    const warehouseSpecificRules: PropertyValidationRule[] = [
      {
        fieldName: 'characteristics.warehouseArea',
        rules: {
          required: true,
          min: 50,
          errorMessage: 'Warehouse area must be at least 50 m²'
        }
      },
      {
        fieldName: 'characteristics.ceilingHeight',
        rules: {
          required: true,
          min: 3,
          max: 30,
          errorMessage: 'Ceiling height must be between 3m and 30m'
        }
      },
      {
        fieldName: 'characteristics.floorLoadCapacity',
        rules: {
          min: 500,
          errorMessage: 'Floor load capacity should be at least 500 kg/m²'
        }
      }
    ];

    return [...baseRules, ...warehouseSpecificRules];
  }

  override getRecommendedMinSurface(): number {
    return 100; // Minimum recommended surface for a warehouse
  }

  override getPriceRangeMultiplier(): number {
    return 0.6; // Warehouses are typically cheaper per m² than residential
  }
}

